import { DEFAULT_PERPEND_GAP_MM, WallModel } from '@bw/core';
import type { BrickId, PlacedBrick, WallSnapshot } from '@bw/core';
import { nameForColour, nextMaterialId, paletteMaterials } from './materials.js';
import type {
  BrickExtras,
  Envelope,
  JoinerySpec,
  Material,
  Opening,
  PlanSpan,
  ProjectMeta,
  RoofSpec,
  UnknownKeys,
  ViewState,
  WallPlacement,
} from './types.js';
import { DEFAULT_JOINERY, DEFAULT_PLACEMENT, DEFAULT_ROOF } from './types.js';

export const APP_ID = 'brickwall-web/1.0.0';

/** One wall in a project: the geometry model plus everything around it. */
export interface Wall {
  readonly id: string;
  name: string;
  placement: WallPlacement;
  baseMaterialId: string;
  envelope: Envelope | null;
  /** The design length in plan, when it differs from the brickwork. */
  span: PlanSpan | null;
  openings: readonly Opening[];
  readonly model: WallModel;
  /** Keys a future version wrote that this build does not understand. */
  extra: UnknownKeys;
  brickExtra: Map<BrickId, UnknownKeys>;
}

export interface WallSnapshotEntry {
  readonly id: string;
  readonly name: string;
  readonly placement: WallPlacement;
  readonly baseMaterialId: string;
  readonly envelope: Envelope | null;
  readonly span: PlanSpan | null;
  readonly openings: readonly Opening[];
  readonly geometry: WallSnapshot;
  readonly extra: UnknownKeys;
  readonly brickExtra: ReadonlyMap<BrickId, UnknownKeys>;
}

/**
 * A restorable picture of the whole project. Cheap, because a WallSnapshot is
 * an array of shared immutable brick references (design document D5).
 */
export interface ProjectSnapshot {
  readonly name: string;
  readonly materials: readonly Material[];
  readonly walls: readonly WallSnapshotEntry[];
  readonly activeWallId: string;
  readonly roof: RoofSpec;
  readonly joinery: JoinerySpec;
}

function timestamp(): string {
  return new Date().toISOString();
}

/**
 * A project holds MANY walls from day one — the essential seam for the 3D
 * house (design document D1, F1). Phase 1 edits one at a time.
 */
export class ProjectDocument {
  #meta: ProjectMeta;
  #materials: Material[];
  #walls: Wall[] = [];
  #activeWallId = '';
  #revision = 0;
  #nextWallNumber = 1;
  extra: UnknownKeys = {};
  /** Anything repaired while loading, for the shell to report. */
  loadWarnings: readonly string[] = [];
  view: ViewState | null = null;
  sheets: readonly UnknownKeys[] = [];
  #roof: RoofSpec = DEFAULT_ROOF;
  #joinery: JoinerySpec = DEFAULT_JOINERY;

  constructor(name = 'Untitled') {
    this.#meta = { name, created: timestamp(), modified: timestamp(), app: APP_ID };
    // The paint tool is only usable with something to paint WITH, so a new
    // project carries the full diaper palette from clause 15.3.
    this.#materials = paletteMaterials();
    this.addWall('Wall 1');
  }

  static empty(name = 'Untitled'): ProjectDocument {
    return new ProjectDocument(name);
  }

  get meta(): ProjectMeta {
    return this.#meta;
  }

  /**
   * Restore metadata verbatim after loading. Opening a project is not a
   * modification, so neither `created` nor `modified` may drift — which is
   * also what makes the format losslessly round-trippable.
   */
  restoreMeta(meta: ProjectMeta): void {
    this.#meta = { ...meta };
  }

  get name(): string {
    return this.#meta.name;
  }

  set name(value: string) {
    this.#meta = { ...this.#meta, name: value };
    this.#touch();
  }

  /** The roof over the building (phase 2, M7). */
  get roof(): RoofSpec {
    return this.#roof;
  }

  setRoof(patch: Partial<RoofSpec>): boolean {
    const next = { ...this.#roof, ...patch };
    if (
      next.style === this.#roof.style &&
      next.pitchDeg === this.#roof.pitchDeg &&
      next.overhangMm === this.#roof.overhangMm &&
      next.materialId === this.#roof.materialId
    ) {
      return false;
    }
    this.#roof = next;
    this.#touch();
    return true;
  }

  /** Defaults for generated windows and doors (phase 2, M7). */
  get joinery(): JoinerySpec {
    return this.#joinery;
  }

  setJoinery(patch: Partial<JoinerySpec>): boolean {
    const next = { ...this.#joinery, ...patch };
    if (
      next.revealMm === this.#joinery.revealMm &&
      next.frameMm === this.#joinery.frameMm &&
      next.glassInsetMm === this.#joinery.glassInsetMm &&
      next.frameColour === this.#joinery.frameColour
    ) {
      return false;
    }
    this.#joinery = next;
    this.#touch();
    return true;
  }

  get materials(): readonly Material[] {
    return this.#materials;
  }

  get walls(): readonly Wall[] {
    return this.#walls;
  }

  get activeWallId(): string {
    return this.#activeWallId;
  }

  /**
   * Aggregate change counter: the document's own revision plus every wall's.
   * Compared against a saved value for O(1) dirty tracking (design D4).
   */
  get revision(): number {
    let total = this.#revision;
    for (const wall of this.#walls) total += wall.model.revision;
    return total;
  }

  #touch(): void {
    this.#revision += 1;
    this.#meta = { ...this.#meta, modified: timestamp() };
  }

  // ---------- walls ----------

  get activeWall(): Wall {
    const wall = this.#walls.find((candidate) => candidate.id === this.#activeWallId);
    if (wall === undefined) throw new Error('The project has no active wall');
    return wall;
  }

  wall(id: string): Wall | undefined {
    return this.#walls.find((candidate) => candidate.id === id);
  }

  /**
   * Choose the wall being edited.
   *
   * Deliberately does NOT touch the revision: which wall you are looking at is
   * view state, not document content, and marking the project modified for it
   * meant that glancing at another elevation was enough to be asked whether to
   * discard your changes. Callers still refresh the UI through the store.
   */
  setActiveWall(id: string): boolean {
    if (this.#walls.every((wall) => wall.id !== id) || id === this.#activeWallId) return false;
    this.#activeWallId = id;
    return true;
  }

  addWall(name?: string, perpendGapMm: number = DEFAULT_PERPEND_GAP_MM): Wall {
    const id = `w${this.#nextWallNumber++}`;
    const wall: Wall = {
      id,
      name: name ?? `Wall ${this.#walls.length + 1}`,
      placement: DEFAULT_PLACEMENT,
      baseMaterialId: this.#materials[0]?.id ?? 'm1',
      envelope: null,
      span: null,
      openings: [],
      model: new WallModel([], perpendGapMm),
      extra: {},
      brickExtra: new Map(),
    };
    this.#walls.push(wall);
    if (this.#activeWallId === '') this.#activeWallId = id;
    this.#touch();
    return wall;
  }

  /**
   * The id `addWall` would hand out next, without consuming it.
   *
   * A loader that builds its own wall — and therefore cannot ask `addWall` for
   * one — needs an id that will not collide, but must not burn a number if the
   * wall it is building turns out not to load. `adoptWall` advances the
   * counter, so peeking here and adopting afterwards keeps the sequence
   * unbroken either way.
   */
  get nextWallId(): string {
    return `w${this.#nextWallNumber}`;
  }

  /** Adopt an already-built wall, used by the loaders. */
  adoptWall(wall: Wall): void {
    this.#walls.push(wall);
    if (this.#activeWallId === '') this.#activeWallId = wall.id;
    const match = /^w(\d+)$/.exec(wall.id);
    if (match) this.#nextWallNumber = Math.max(this.#nextWallNumber, Number(match[1]) + 1);
    this.#touch();
  }

  removeWall(id: string): boolean {
    if (this.#walls.length <= 1) return false;
    const index = this.#walls.findIndex((wall) => wall.id === id);
    if (index === -1) return false;
    this.#walls.splice(index, 1);
    if (this.#activeWallId === id) {
      this.#activeWallId = (this.#walls[Math.min(index, this.#walls.length - 1)] as Wall).id;
    }
    this.#touch();
    return true;
  }

  /**
   * Where a wall sits in plan (phase 2, M5).
   *
   * The first thing in the codebase to write a placement — it has been
   * defaulted and round-tripped since M1 waiting for the plan editor. Returns
   * false when nothing moved, which is what lets the caller's undo checkpoint
   * be abandoned rather than recording an empty step.
   */
  setWallPlacement(id: string, placement: WallPlacement): boolean {
    const wall = this.wall(id);
    if (wall === undefined) return false;
    const current = wall.placement;
    if (
      current.origin.x === placement.origin.x &&
      current.origin.y === placement.origin.y &&
      current.origin.z === placement.origin.z &&
      current.rotationDeg === placement.rotationDeg &&
      current.thicknessMm === placement.thicknessMm
    ) {
      return false;
    }
    wall.placement = placement;
    this.#touch();
    return true;
  }

  renameWall(id: string, name: string): boolean {
    const wall = this.wall(id);
    if (wall === undefined || wall.name === name) return false;
    wall.name = name;
    this.#touch();
    return true;
  }

  // ---------- materials ----------

  material(id: string): Material | undefined {
    return this.#materials.find((candidate) => candidate.id === id);
  }

  /** The colour a brick paints with, falling back to the first material. */
  colourFor(brick: PlacedBrick): string {
    return this.material(brick.materialId)?.colour ?? this.#materials[0]?.colour ?? '#B5523B';
  }

  addMaterial(colour: string, name?: string): Material {
    const hex = colour.toUpperCase();
    const existing = this.#materials.find((material) => material.colour === hex);
    if (existing !== undefined) return existing;
    const id = nextMaterialId(this.#materials);
    const material = { id, name: name ?? nameForColour(hex), colour: hex, texture: null, roughness: null };
    this.#materials.push(material);
    this.#touch();
    return material;
  }

  /** Remove a material, provided nothing is painted with it. */
  removeMaterial(id: string): boolean {
    if (this.#materials.length <= 1) return false;
    if ((this.materialUsage().get(id) ?? 0) > 0) return false;
    const index = this.#materials.findIndex((material) => material.id === id);
    if (index === -1) return false;
    this.#materials.splice(index, 1);
    this.#touch();
    return true;
  }

  setMaterials(materials: readonly Material[]): void {
    this.#materials = [...materials];
    this.#touch();
  }

  updateMaterial(id: string, patch: Partial<Omit<Material, 'id'>>): boolean {
    const index = this.#materials.findIndex((material) => material.id === id);
    if (index === -1) return false;
    this.#materials[index] = { ...(this.#materials[index] as Material), ...patch };
    this.#touch();
    return true;
  }

  /** Materials actually referenced by at least one brick, with counts. */
  materialUsage(): Map<string, number> {
    const usage = new Map<string, number>();
    for (const wall of this.#walls) {
      for (const brick of wall.model.bricks) {
        usage.set(brick.materialId, (usage.get(brick.materialId) ?? 0) + 1);
      }
    }
    return usage;
  }

  // ---------- snapshots ----------

  snapshot(): ProjectSnapshot {
    return {
      name: this.#meta.name,
      materials: [...this.#materials],
      activeWallId: this.#activeWallId,
      roof: this.#roof,
      joinery: this.#joinery,
      walls: this.#walls.map((wall) => ({
        id: wall.id,
        name: wall.name,
        placement: wall.placement,
        baseMaterialId: wall.baseMaterialId,
        envelope: wall.envelope,
        span: wall.span,
        openings: wall.openings,
        geometry: wall.model.snapshot(),
        extra: wall.extra,
        brickExtra: new Map(wall.brickExtra),
      })),
    };
  }

  restore(snapshot: ProjectSnapshot): void {
    this.#meta = { ...this.#meta, name: snapshot.name };
    this.#materials = [...snapshot.materials];
    this.#roof = snapshot.roof ?? DEFAULT_ROOF;
    this.#joinery = snapshot.joinery ?? DEFAULT_JOINERY;
    const existing = new Map(this.#walls.map((wall) => [wall.id, wall]));
    this.#walls = snapshot.walls.map((entry) => {
      const wall = existing.get(entry.id);
      const model = wall?.model ?? new WallModel([], entry.geometry.perpendGapMm);
      model.restoreSnapshot(entry.geometry);
      return {
        id: entry.id,
        name: entry.name,
        placement: entry.placement,
        baseMaterialId: entry.baseMaterialId,
        envelope: entry.envelope,
        span: entry.span ?? null,
        openings: entry.openings,
        model,
        extra: entry.extra,
        brickExtra: new Map(entry.brickExtra),
      };
    });
    this.#activeWallId =
      this.#walls.find((wall) => wall.id === snapshot.activeWallId)?.id ??
      (this.#walls[0] as Wall | undefined)?.id ??
      '';
    this.#touch();
  }

  /** Total bricks across every wall — used by the history budget. */
  get brickCount(): number {
    let total = 0;
    for (const wall of this.#walls) total += wall.model.size;
    return total;
  }
}

export type { BrickExtras };
